/**
 * CMS shape contracts — TypeScript side of SCHEMA-06.
 *
 * Source of truth: `app.gamedevbuddy.com/server/src/services/cms/read.rs`
 * (NewsArticle struct) and `app.gamedevbuddy.com/server/src/server/handlers/platform/cms.rs`
 * (CmsListResponse<T>).
 *
 * The Rust side serializes via `#[serde(rename_all = "camelCase")]`. This file
 * defines the TS-side parsers (Zod) and the inferred TS interfaces. Future
 * React surfaces (Phase 4+) import from this file:
 *
 *   import { NewsArticle, NewsArticleSchema, CmsListResponse } from '@/core/marketing/models/cmsTypes'
 *
 * The accompanying contract test (`__tests__/cmsTypes.contract.test.ts`)
 * catches the most common drift modes at TS build time (renamed field,
 * snake_case sneaking in). A live-API test against a running sidecar is
 * deferred to Phase 4 News, where a real DB row exists to fetch.
 */

import { z } from 'zod'

// ── cms_status enum (Postgres ENUM) ────────────────────────────────────────

export const CmsStatusSchema = z.enum(['draft', 'published'])
export type CmsStatus = z.infer<typeof CmsStatusSchema>

// ── NewsArticle (cms_news_articles row, public read shape) ─────────────────

/**
 * Canonical News article shape. Mirrors the Rust `NewsArticle` struct in
 * server/src/services/cms/read.rs. Field renames:
 *   body_html       -> bodyHtml
 *   hero_image_url  -> heroImageUrl
 *   gdb_module      -> gdbModule
 *   category        -> category   (no rename; Plan 04-01 / NEWS-02)
 *   published_at    -> publishedAt (ISO string)
 *   created_at      -> createdAt   (ISO string)
 *   updated_at      -> updatedAt   (ISO string)
 *
 * `.strict()` rejects extra/unexpected fields. Combined with explicit
 * camelCase field names this catches the snake_case drift case (a fixture
 * with `body_html` will fail because `body_html` is unexpected AND
 * `bodyHtml` is missing).
 *
 * Plan 04-01 (NEWS-02): `category` is `.nullable().optional()` chained with
 * a `.transform(v => v ?? null)` so the schema accepts BOTH the new wire
 * shape (`category: string | null`) AND the legacy pre-Phase-4 shape that
 * omits the field entirely. The transform normalises `undefined` to `null`
 * so callers always see `string | null`. No enum constraint here — the
 * Plan 04-08 migration script writes only Payload's 5 known values
 * (announcement / release / update / tutorial / community); the server is
 * the source of truth.
 */
export const NewsArticleSchema = z
  .object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    bodyHtml: z.string(),
    excerpt: z.string().nullable(),
    heroImageUrl: z.string().nullable(),
    author: z.string().nullable(),
    gdbModule: z.string().nullable(),
    category: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    // Issue #156 — multi-value categories. Optional+default so older
    // sidecar binaries (which only emit `category`) keep validating;
    // newer consumers should prefer this field.
    categories: z.array(z.string()).optional().default([]),
    tags: z.array(z.string()),
    /// Always `'published'` on the wire from the public read paths (the
    /// SQL filters status='published'). Surfaced so the admin console
    /// (CollectionListPage status counter + per-row dot) can read the
    /// same shape. Optional with default so a backend that predates the
    /// status-projection change still validates — the field becomes
    /// canonical once the new sidecar binary is rolled out.
    status: z.string().optional().default('published'),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()

export type NewsArticle = z.infer<typeof NewsArticleSchema>

// ── RoadmapItem (cms_roadmap row, public read shape) ───────────────────────

/**
 * Canonical roadmap item shape. Mirrors the Rust `RoadmapItem` struct in
 * server/src/services/cms/read.rs (Plan 05-01). Field renames:
 *   body_html      -> bodyHtml
 *   status_bucket  -> statusBucket
 *   gdb_module     -> gdbModule
 *   sort_order     -> sortOrder
 *   published_at   -> publishedAt (ISO string, nullable)
 *   created_at     -> createdAt   (ISO string)
 *   updated_at     -> updatedAt   (ISO string)
 *
 * `.strict()` rejects extra/unexpected fields. Combined with explicit
 * camelCase field names this catches snake_case drift.
 *
 * Phase 5 (ROAD-01) Plan 05-02 — TypeScript half of ROAD-01.
 */
export const RoadmapItemSchema = z
  .object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    bodyHtml: z.string(),
    excerpt: z.string().nullable(),
    statusBucket: z.enum(['now', 'next', 'later', 'done']),
    gdbModule: z.string().nullable(),
    sortOrder: z.number().int(),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()

export type RoadmapItem = z.infer<typeof RoadmapItemSchema>

// ── Generic list response envelope ─────────────────────────────────────────

/**
 * Shape returned by `GET /api/cms/{collection}` for every CMS read endpoint.
 * Phase 2 ships the news-articles handler that returns this shape; Phase 4-9
 * slices add their own list endpoints, all returning the same shape.
 */
export interface CmsListResponse<T> {
  items: T[]
  total: number
}

/**
 * Build a Zod schema for `CmsListResponse<T>` given the item schema.
 * Use:
 *   const NewsArticleListSchema = cmsListResponseSchema(NewsArticleSchema)
 */
export function cmsListResponseSchema<TItem extends z.ZodTypeAny>(item: TItem) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
  })
}

// ── Pre-built list-response schemas per collection ─────────────────────────

/**
 * Plan 05-02 (ROAD-01) — convenience schema for `GET /api/cms/roadmap`
 * (public read) and `GET /api/cms/admin/roadmap` (super-admin read).
 * Both endpoints return the same `{ items, total }` envelope shape.
 */
export const RoadmapItemListSchema = cmsListResponseSchema(RoadmapItemSchema)

// ── ChangelogEntry (cms_changelog row, public read shape) ──────────────────

/**
 * Canonical Changelog entry shape. Mirrors the Rust `ChangelogEntry` struct in
 * server/src/services/cms/read.rs (Plan 06-01). Field renames:
 *   body_html      -> bodyHtml
 *   gdb_module     -> gdbModule
 *   released_at    -> releasedAt   (ISO string, NOT NULL)
 *   change_type    -> changeType   (cms_change_type enum)
 *   published_at   -> publishedAt  (ISO string, nullable)
 *   created_at     -> createdAt    (ISO string)
 *   updated_at     -> updatedAt    (ISO string)
 *
 * `.strict()` rejects extra/unexpected fields. Combined with explicit camelCase
 * field names this catches the snake_case drift case (a fixture with
 * `change_type` will fail because `change_type` is unexpected AND `changeType`
 * is missing).
 *
 * `links` is typed as `z.array(z.unknown())` for v1 — the JSONB column carries
 * `{ label, url }` objects but the TS layer does NOT pre-validate that shape.
 * The TagListInput v1 stores entries as `"label|url"` strings; a richer JSON
 * editor (with strict {label,url} validation) is deferred to a future phase
 * per CONTEXT.md decision lock.
 *
 * Phase 6 (CHG-01) Plan 06-02 — TypeScript half of CHG-01.
 */
export const ChangelogEntrySchema = z
  .object({
    id: z.string().uuid(),
    slug: z.string(),
    version: z.string(),
    summary: z.string().nullable(),
    bodyHtml: z.string(),
    gdbModule: z.string().nullable(),
    releasedAt: z.string().datetime(),
    changeType: z.enum([
      'added',
      'changed',
      'fixed',
      'deprecated',
      'removed',
      'security',
    ]),
    links: z.array(z.unknown()),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()

export type ChangelogEntry = z.infer<typeof ChangelogEntrySchema>

/**
 * Plan 06-02 (CHG-01) — convenience schema for `GET /api/cms/changelog`
 * (public read) and `GET /api/cms/admin/changelog` (super-admin read).
 * Both endpoints return the same `{ items, total }` envelope shape.
 */
export const ChangelogEntryListSchema = cmsListResponseSchema(ChangelogEntrySchema)

// ── DocPage (cms_docs row, public read shape) ──────────────────────────────

/**
 * Phase 7 (DOCS-01) Plan 07-02 — TypeScript half of DOCS-01.
 *
 * Mirrors the Rust `DocPage` struct in `server/src/services/cms/read.rs`
 * (Plan 07-01) — camelCase via `#[serde(rename_all = "camelCase")]`. Field
 * renames:
 *   body_md        -> bodyMd        (markdown source — Docusaurus consumes this in Phase 15)
 *   body_html      -> bodyHtml      (rendered HTML — populated by the editor + ammonia-sanitised)
 *   parent_slug    -> parentSlug    (nullable — for nested doc trees)
 *   sort_order     -> sortOrder     (NOT NULL, default 0)
 *   sidebar_label  -> sidebarLabel  (nullable — short label distinct from title)
 *   surfaces       -> surfaces      (TEXT[] NOT NULL — at least one of website/app)
 *   category       -> category      (nullable — top-level grouping, free-form)
 *   published_at   -> publishedAt   (ISO string, nullable)
 *   created_at     -> createdAt     (ISO string)
 *   updated_at     -> updatedAt     (ISO string)
 *
 * `surfaces` is constrained to the literal tuple via `z.enum(DOCS_SURFACES)`.
 * Plan 07-01's API also rejects empty `surfaces` arrays at the service layer;
 * this schema mirrors that with `.min(1)`.
 *
 * Note: this schema does NOT use `.strict()` — the admin write endpoints may
 * return additional fields (`status` etc.) that the public read shape doesn't
 * expose. Adding `.strict()` would force every consumer to keep up with the
 * write shape over time.
 */
export const DOCS_SURFACES = ['website', 'app'] as const
export type DocsSurface = (typeof DOCS_SURFACES)[number]

export const DocPageSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  title: z.string().min(1),
  bodyMd: z.string(),
  bodyHtml: z.string(),
  parentSlug: z.string().nullable(),
  sortOrder: z.number().int(),
  sidebarLabel: z.string().nullable(),
  surfaces: z.array(z.enum(DOCS_SURFACES)).min(1),
  category: z.string().nullable(),
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type DocPage = z.infer<typeof DocPageSchema>

/**
 * Plan 07-02 (DOCS-01) — convenience schema for `GET /api/cms/docs`
 * (public read) and `GET /api/cms/admin/docs` (super-admin read).
 * Both endpoints return the same `{ items, total }` envelope shape.
 */
export const DocPageListSchema = cmsListResponseSchema(DocPageSchema)
export type DocPageList = z.infer<typeof DocPageListSchema>

// ── ProductPage block-type schemas (Phase 8 PROD-01 / Plan 08-02) ──────────
//
// 5 LOCKED block types. The set is fixed for Phase 8 — adding new block types
// is deferred to Phase 9 (Pages slice). Definitions are verbatim from
// `.planning/phases/08-productpages-cms-slice/08-CONTEXT.md`.
//
// Block-type schemas are intentionally NOT `.strict()`. Future phases may add
// optional fields per block type without breaking existing rows. The
// `ProductPageBlockSchema` discriminatedUnion narrows on `type` regardless of
// strictness, and `ProductPageSchema` IS `.strict()` so snake_case drift at
// the top level is still caught.

/**
 * Hero block — top-of-page banner with heading, optional subheading, and an
 * optional CTA button. Used as the first block on /pricing and /modules.
 */
export const HeroBlockSchema = z.object({
  type: z.literal('hero'),
  heading: z.string(),
  subheading: z.string().optional(),
  ctaText: z.string().optional(),
  ctaUrl: z.string().optional(),
})
export type HeroBlock = z.infer<typeof HeroBlockSchema>

/**
 * Feature list block — heading + ≥1 items, each with title + description and
 * optional icon name. Renders as a grid or vertical list depending on count.
 */
export const FeatureListBlockSchema = z.object({
  type: z.literal('feature-list'),
  heading: z.string().optional(),
  items: z
    .array(
      z.object({
        icon: z.string().optional(),
        title: z.string(),
        description: z.string(),
      }),
    )
    .min(1),
})
export type FeatureListBlock = z.infer<typeof FeatureListBlockSchema>

/**
 * Pricing tier block — single tier card. Page-level pricing rows hold one
 * `pricing-tier` per tier (Free / Pro / Studio). `priceCents=null` means
 * "free" or "contact us"; the renderer displays accordingly. `currency`
 * defaults to USD (Phase 8 is USD-only; multi-currency deferred). The
 * `highlighted` flag styles the tier card with an accent ring.
 */
export const PricingTierBlockSchema = z.object({
  type: z.literal('pricing-tier'),
  name: z.string(),
  priceCents: z.number().int().nullable(),
  currency: z.string().default('USD'),
  period: z.enum(['monthly', 'yearly', 'one-time']),
  features: z.array(z.string()),
  ctaText: z.string(),
  ctaUrl: z.string(),
  highlighted: z.boolean().default(false),
})
export type PricingTierBlock = z.infer<typeof PricingTierBlockSchema>

/**
 * Screenshot grid block — heading + ≥1 image items. Each item requires
 * `altText` (a11y); `caption` is optional. Image URLs are written by the
 * migration script in Plan 08-07 to use the `/media/...` prefix per Plan
 * 03-08's MED-04 contract.
 */
export const ScreenshotGridBlockSchema = z.object({
  type: z.literal('screenshot-grid'),
  heading: z.string().optional(),
  items: z
    .array(
      z.object({
        imageUrl: z.string(),
        caption: z.string().optional(),
        altText: z.string(),
      }),
    )
    .min(1),
})
export type ScreenshotGridBlock = z.infer<typeof ScreenshotGridBlockSchema>

/**
 * CTA section block — bottom-of-page conversion block. Heading + optional
 * body + a single CTA button. Typically renders with the brand-accent
 * background to draw the eye.
 */
export const CtaSectionBlockSchema = z.object({
  type: z.literal('cta-section'),
  heading: z.string(),
  body: z.string().optional(),
  ctaText: z.string(),
  ctaUrl: z.string(),
})
export type CtaSectionBlock = z.infer<typeof CtaSectionBlockSchema>

/**
 * Rich-text block — Plan 04.5-09 (Detail Page slice), the 6th block type
 * and the first union extension since the Phase 8 lock. A prose section
 * carrying an HTML string rendered like the existing cms_products
 * description (dangerouslySetInnerHTML behind the server-side
 * sanitize_block_text pass — every write runs the html field through
 * sanitize_html in services/cms/admin.rs, mirroring CON-05).
 */
export const RichTextBlockSchema = z.object({
  type: z.literal('rich-text'),
  html: z.string(),
})
export type RichTextBlock = z.infer<typeof RichTextBlockSchema>

/**
 * Discriminated union over the 6 locked block types (5 from the Phase 8
 * lock + `rich-text` from Plan 04.5-09). The `type` field is the
 * discriminator; an unknown discriminator (e.g. `{ type: 'unicorn' }`) yields
 * a Zod `invalid_union_discriminator` issue. Used by `ProductPageSchema`
 * inside `bodyBlocks: z.array(ProductPageBlockSchema)`, by the products
 * `detailBlocks` column, and by Plan 08-03's BlocksEditor renderer + Plan
 * 08-05's per-block public renderers.
 *
 * EXTENSION SEAM: adding a block type = (1) a schema + union member here,
 * (2) a sub-form in console components/fields/blocks/ + BlocksEditor
 * registration, (3) a public renderer in marketing components/blocks/ +
 * BlockRenderer case, (4) PRODUCT_PAGE_BLOCK_TYPES + a sanitize case in
 * server services/cms/admin.rs. TS exhaustiveness fails closed on (2) and
 * (3) the moment (1) lands.
 */
export const ProductPageBlockSchema = z.discriminatedUnion('type', [
  HeroBlockSchema,
  FeatureListBlockSchema,
  PricingTierBlockSchema,
  ScreenshotGridBlockSchema,
  CtaSectionBlockSchema,
  RichTextBlockSchema,
])
export type ProductPageBlock = z.infer<typeof ProductPageBlockSchema>

// ── ProductPage (cms_product_pages row, public read shape) ─────────────────

/**
 * Phase 8 (PROD-01) Plan 08-02 — TypeScript half of PROD-01.
 *
 * Mirrors the Rust `ProductPage` struct in `server/src/services/cms/read.rs`
 * (Plan 08-01) — camelCase via `#[serde(rename_all = "camelCase")]`. Field
 * renames:
 *   body_blocks    -> bodyBlocks      (JSONB array of ProductPageBlock)
 *   slug_target    -> slugTarget      ('pricing' | 'modules' | 'page')
 *   gdb_module     -> gdbModule       (nullable; module-specific pages)
 *   price_cents    -> priceCents      (nullable; headline price, NOT per-tier)
 *   billing_period -> billingPeriod   (nullable; headline period)
 *   published_at   -> publishedAt     (ISO string, nullable)
 *   created_at     -> createdAt       (ISO string)
 *   updated_at     -> updatedAt       (ISO string)
 *
 * `meta` is `z.record(z.string(), z.unknown())` — the JSONB column carries SEO meta
 * (description, ogImage) used by RouteHead in Plan 08-05; structural shape is
 * open-ended on purpose (mirrors NewsArticle's relaxed meta column).
 *
 * `.strict()` rejects extra/unexpected fields. Combined with explicit
 * camelCase field names this catches snake_case drift at the top level (a
 * fixture with `body_blocks` will fail because it's both unexpected AND
 * `bodyBlocks` is missing).
 */
export const ProductPageSchema = z
  .object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    bodyBlocks: z.array(ProductPageBlockSchema),
    slugTarget: z.string(),
    gdbModule: z.string().nullable(),
    priceCents: z.number().int().nullable(),
    billingPeriod: z.string().nullable(),
    meta: z.record(z.string(), z.unknown()),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()

export type ProductPage = z.infer<typeof ProductPageSchema>

/**
 * Plan 08-02 (PROD-01) — convenience schema for `GET /api/cms/product-pages`
 * (public read) and `GET /api/cms/admin/product-pages` (super-admin read).
 * Both endpoints return the same `{ items, total }` envelope shape.
 */
export const ProductPageListSchema = cmsListResponseSchema(ProductPageSchema)
export type ProductPageList = z.infer<typeof ProductPageListSchema>

// ── Page (cms_pages row, public read shape) ────────────────────────────────
//
// Phase 9 (PG-01) Plan 09-02 — TypeScript half of PG-01.
//
// Mirrors the Rust `Page` struct in `server/src/services/cms/read.rs`
// (Plan 09-01) — camelCase via `#[serde(rename_all = "camelCase")]`. Field
// renames:
//   body_blocks    -> bodyBlocks      (JSONB array of ProductPageBlock — Phase 8 reused)
//   published_at   -> publishedAt     (ISO string, nullable)
//   created_at     -> createdAt       (ISO string)
//   updated_at     -> updatedAt       (ISO string)
//
// `meta` is `z.record(z.string(), z.unknown())` matching the Phase 2
// cms_pages.meta JSONB column (open-ended SEO meta — description, ogImage).
// Phase 10 RouteHead consumers read meta if present.
//
// Page does NOT have a slug_target / gdb_module / price_cents / billing_period
// field — pages are fully described by title + bodyBlocks. The legacy
// hero_block + sections JSONB columns from Phase 2's CREATE TABLE are kept
// for backward-compat (see Plan 09-01 schema notes) but are NOT in this wire
// shape — Phase 10 consumers read body_blocks only.
//
// `bodyBlocks` reuses `ProductPageBlockSchema` verbatim per the locked
// 5-block-type set (hero / feature-list / pricing-tier / screenshot-grid /
// cta-section). NO new block schemas are introduced in Phase 9.
//
// `.strict()` rejects extra/unexpected fields. A fixture with `body_blocks`
// (snake_case) fails because both: it's unexpected AND `bodyBlocks` is
// missing.
export const PageSchema = z
  .object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    bodyBlocks: z.array(ProductPageBlockSchema),
    meta: z.record(z.string(), z.unknown()),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()

export type Page = z.infer<typeof PageSchema>

/**
 * Plan 09-02 (PG-01) — convenience schema for `GET /api/cms/pages`
 * (public read) and `GET /api/cms/admin/pages` (super-admin read).
 * Both endpoints return the same `{ items, total }` envelope shape.
 */
export const PageListSchema = cmsListResponseSchema(PageSchema)
export type PageList = z.infer<typeof PageListSchema>
